import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import { ExperienceInputField } from '../form'
import ExperienceDatePicker from '../form/ExperienceDatePicker'
import QuestHOC from './QuestHOC'
import {
  UTCFormatDate,
  UTCformatDateInPlan,
  getCurrentDate,
} from '../../utils/date'
import moment from 'moment-timezone'
import {
  queueAutosaveOnBlur,
  autosaveAfterFieldChange,
  runQueuedFormikSave,
  FormikDirtyReporter,
} from './questAutosave'

const isValidCompleteDate = (dateString) => {
  if (!dateString || dateString === '') {
    return false
  }
  const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/
  if (!dateRegex.test(dateString)) {
    return false
  }
  return moment(dateString, 'MM/DD/YYYY', true).isValid()
}

const isValidUrl = (value) => {
  try {
    new URL(value)
    return true
  } catch (e) {
    return false
  }
}

const getInitialValues = (questDetail) => ({
  orientationCallLink: questDetail?.orientationCall?.link || '',
  orientationCallDate: questDetail?.orientationCall?.date
    ? UTCFormatDate(questDetail.orientationCall.date)
    : '',
})

const QuestOrientationCall = ({ questDetail, updateQuestDetails }) => {
  const [initialValues, setInitialValues] = useState(() =>
    getInitialValues(questDetail),
  )
  const valuesRef = useRef(initialValues)

  useEffect(() => {
    const next = getInitialValues(questDetail)
    setInitialValues(next)
    valuesRef.current = next
  }, [questDetail])

  const buildPayload = (values) => ({
    orientationCall: {
      link: values.orientationCallLink || '',
      date:
        values.orientationCallDate &&
        isValidCompleteDate(values.orientationCallDate)
          ? UTCformatDateInPlan(values.orientationCallDate)
          : '',
    },
  })

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validate={(values) => {
        const errors = {}

        if (
          values.orientationCallLink &&
          !isValidUrl(values.orientationCallLink)
        ) {
          errors.orientationCallLink = 'Invalid URL'
        }

        if (
          values.orientationCallDate &&
          !isValidCompleteDate(values.orientationCallDate)
        ) {
          errors.orientationCallDate =
            'Orientation Call Date must be a complete and valid date (MM/DD/YYYY).'
        }

        return errors
      }}
      onSubmit={async (values, { resetForm }) => {
        await runQueuedFormikSave({
          values,
          valuesRef,
          buildPayload,
          resetForm,
          setInitialValues,
          save: (payload) => updateQuestDetails(payload, { silent: true }),
        })
      }}>
      {(formik) => {
        const { handleSubmit, submitForm, dirty } = formik
        valuesRef.current = formik.values
        const fieldBlur = () => queueAutosaveOnBlur(formik)

        return (
          <form onSubmit={handleSubmit} className='orientation-call-form'>
            <FormikDirtyReporter id='prep-orientation' dirty={dirty} />
            <div className='experience-f-row'>
              <div className='experience-f'>
                <ExperienceInputField
                  id='orientationCallLink'
                  name='orientationCallLink'
                  label='Orientation Call Link'
                  size='lg'
                  placeholder='Add link'
                  showErrorMessage
                  onBlur={fieldBlur}
                />
              </div>
              <div className='experience-f'>
                <ExperienceDatePicker
                  name='orientationCallDate'
                  label='Orientation Call Date'
                  showErrorMessage
                  className='experience-f-control'
                  labelProps={{ className: 'experience-f-label' }}
                  displayFlex={false}
                  minimumDate={getCurrentDate()}
                  maximumDate={getCurrentDate().add(10, 'years')}
                  onValueChange={(_next, setValueResult) =>
                    autosaveAfterFieldChange(formik, setValueResult)
                  }
                />
              </div>
            </div>
            <button type='submit' hidden disabled={!submitForm} aria-hidden />
          </form>
        )
      }}
    </Formik>
  )
}

QuestOrientationCall.propTypes = {
  questDetail: PropTypes.shape({
    orientationCall: PropTypes.shape({
      link: PropTypes.string,
      date: PropTypes.string,
    }),
  }),
  updateQuestDetails: PropTypes.func,
  isFormSubmitting: PropTypes.bool,
}

export default QuestHOC(QuestOrientationCall)
